<script lang="ts">
import {defineComponent, ref, watch, onMounted} from 'vue';
import {translation, useTranslationStore} from "~/plugins/store/translations";
import {useNuxtApp} from "#app";
import {
  createDefaultHouseGoods,
  type HouseGoods,
  createDefaultValidationHouseGoodsStatus,
  type ValidationHouseGoodsStatus
} from "~/types/House";
import HouseGoodsTypeSelect from "~/components/house/HouseGoodsTypeSelect.vue";
import HouseAmountInsured from "~/components/house/HouseAmountInsured.vue";
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  components: {HouseGoodsTypeSelect, HouseAmountInsured},
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    modelValue: {
      type: Object as () => HouseGoods | null,
      default: () => null,
      required: false
    }
  },
  setup(props, {emit}) {
    const dialog = ref(false);
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const houseGoodsForm = ref<HouseGoods>(createDefaultHouseGoods());
    const {$toast} = useNuxtApp();
    const validationHouseGoodsStatus = ref<ValidationHouseGoodsStatus>(createDefaultValidationHouseGoodsStatus());

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
    });

    watch(() => props.showDialog, (newValue) => {
      if (newValue) {
        dialog.value = true;
        houseGoodsForm.value = createDefaultHouseGoods(); // Reset form when dialog is opened
        validationHouseGoodsStatus.value = createDefaultValidationHouseGoodsStatus(); // Reset validation status
      }
    });

    const handleCloseDialog = (response: boolean) => {
      if (response) {
        const isHouseGoodsFormValid = (() => {
          let allValid = true;
          const houseGoodsFields = [
            'amount_insured',
            'goods_type',
          ];
          houseGoodsFields.forEach(field => {
            if (!validationHouseGoodsStatus.value[field]) {
              window.dispatchEvent(new CustomEvent('validate-house', {detail: field}));
              allValid = false;
            }
          });
          return allValid;
        })();
        if (!isHouseGoodsFormValid) {
          $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
          return;
        }
        emit('update:modelValue', {...houseGoodsForm.value});
      }
      emit('response');
      dialog.value = false;
    };

    const handleValidationHouseGoodsStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      validationHouseGoodsStatus.value[field] = isValid;
    };

    return {
      dialog,
      translationList,
      translation,
      handleCloseDialog,
      houseGoodsForm,
      handleValidationHouseGoodsStatus
    };
  },
});
</script>

<template>
  <v-dialog
      v-model="dialog"
      persistent
      :close-on-back="false"
      transition="dialog-top-transition"
  >
    <v-card rounded="lg">
      <v-card-text class="fw-700 fs-24">
        {{ translation(translationList.insure_goods, 'Asigură bunuri extra') }}
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text>
        Adaugă bunurile suplimentare pe care dorești să le protejezi.
      </v-card-text>

      <v-card-text>
        <v-row>
          <v-col cols="12" sm="12" md="6">
            <house-goods-type-select v-model="houseGoodsForm.goods_type"
                                     @validation-house-status="handleValidationHouseGoodsStatus"></house-goods-type-select>
          </v-col>
          <v-col cols="12" sm="12" md="6">
            <house-amount-insured v-model="houseGoodsForm.amount_insured"
                                  @validation-house-status="handleValidationHouseGoodsStatus"></house-amount-insured>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="mx-4 mb-4">
        <v-row>
          <v-col cols="12" sm="12" md="6">
            <v-btn
                class="w-100"
                color="primary-light"
                rounded="lg"
                size="large"
                variant="flat"
                @click="handleCloseDialog(false)"
            >
              {{ translation(translationList.not_now, 'Nu acum') }}
            </v-btn>
          </v-col>
          <v-col cols="12" sm="12" md="6">
            <v-btn
                class="w-100"
                color="primary"
                rounded="lg"
                size="large"
                variant="flat"
                @click="handleCloseDialog(true)"
            >
              {{ translation(translationList.save, 'Adaugă bun') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
</style>